import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API, errText, initials, useHeaders, usePortal, useSubmit } from './kit';

const SIZE = 320;

const Cropper = ({ onSaved }) => {
  const headers = useHeaders();
  const { toast, closeDrawer } = usePortal();
  const canvas = useRef(null);
  const [img, setImg] = useState(null);
  const [z, setZ] = useState({ zoom: 1, x: 0, y: 0 });
  const [busy, run] = useSubmit();
  useEffect(() => {
    if (!img || !canvas.current) return;
    const ctx = canvas.current.getContext('2d');
    const s = Math.max(SIZE / img.width, SIZE / img.height) * z.zoom;
    const w = img.width * s; const h = img.height * s;
    ctx.clearRect(0, 0, SIZE, SIZE);
    ctx.drawImage(img, (SIZE - w) / 2 + (z.x / 100) * (w - SIZE) / 2, (SIZE - h) / 2 + (z.y / 100) * (h - SIZE) / 2, w, h);
  }, [img, z]);
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type) || f.size > 5 * 1024 * 1024) { toast('Choose a JPEG, PNG or WebP image up to 5 MB.'); return; }
    const r = new FileReader();
    r.onload = () => { const i = new Image(); i.onload = () => setImg(i); i.onerror = () => toast('That photo could not be read. Try another file.'); i.src = r.result; };
    r.readAsDataURL(f);
  };
  const save = () => run(async () => {
    try {
      const r = await axios.post(`${API}/api/account/me/avatar`, { data_url: canvas.current.toDataURL('image/jpeg', 0.86) }, { headers });
      onSaved(r.data.photo_url); toast('Profile photo updated.'); closeDrawer();
    } catch (e) { toast(errText(e)); }
  });
  const slide = (k, label, min, max, step = 1) => (<><label htmlFor={`crop-${k}`}>{label}</label>
    <input id={`crop-${k}`} type="range" min={min} max={max} step={step} value={z[k]} onChange={(e) => setZ({ ...z, [k]: Number(e.target.value) })} style={{ marginBottom: 15 }} data-testid={`crop-${k}`} /></>);
  return (
    <div>
      <label htmlFor="profile-file">Choose a photo</label>
      <input id="profile-file" type="file" accept="image/jpeg,image/png,image/webp" onChange={pick} data-testid="photo-file" />
      <p className="muted">JPEG, PNG or WebP · Up to 5 MB · Visible to your Gleam Guru team</p>
      {img && <div id="crop-controls"><canvas id="crop-canvas" ref={canvas} width={SIZE} height={SIZE} aria-label="Profile photo crop preview" />
        {slide('zoom', 'Zoom', 1, 3, 0.05)}{slide('x', 'Horizontal position', -100, 100)}{slide('y', 'Vertical position', -100, 100)}
        <button className="primary" onClick={save} disabled={busy} data-testid="photo-save">{busy ? 'Saving…' : 'Use this photo'}</button></div>}
    </div>
  );
};

export const PhotoPanel = () => {
  const { user, updateUserLocal } = useAuth();
  const headers = useHeaders();
  const { openDrawer, toast } = usePortal();
  const set = (photo_url) => updateUserLocal({ photo_url });
  const remove = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    try { await axios.delete(`${API}/api/account/me/avatar`, { headers }); set(null); toast('Profile photo removed.'); } catch (e) { toast(errText(e)); }
  };
  return (
    <section className="panel photo-account" data-testid="photo-panel">
      <div className="profile-photo">{user?.photo_url ? <img src={user.photo_url} alt="Your profile" /> : initials(user?.full_name)}</div>
      <div><h2>Your profile photo</h2><p>Optional. Visible to your Gleam Guru team.</p>
        <button className="outline" onClick={() => openDrawer({ title: 'Your profile photo', content: <Cropper onSaved={set} /> })} data-testid="photo-upload">{user?.photo_url ? 'Change photo' : 'Upload photo'}</button>
        {user?.photo_url && <button className="text-button" onClick={remove} data-testid="photo-remove">Remove photo</button>}</div>
    </section>
  );
};

export default PhotoPanel;
